/** Partner sign-in: the MSP ID, the email and the password, then the authenticator. Or the partner's own identity provider. */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { AuthAlert, AuthShell } from "../kit/Auth";
import { Icon } from "../kit/Icon";
import { Button, PasswordInput } from "../kit/ui";

export const PARTNER_SCENE = { you: "you · partner", target: "partner portal", support: "Every customer tenant you manage, reached with the access each customer chose to share." };
export const PARTNER_HELP = "For Inlinea partners. Every action in a customer tenant carries your name in both audit trails.";

function remembered(): string {
  try {
    return localStorage.getItem("inlinea.msp_id") ?? "";
  } catch {
    return "";
  }
}

export function SignIn() {
  const [sp] = useSearchParams();
  const error = sp.get("error");
  const ret = sp.get("return") ?? "/";
  const dev = sp.get("dev") === "1";
  const [msp, setMsp] = useState(sp.get("msp") ?? remembered());
  const [busy, setBusy] = useState(false);
  // The identity provider button shows only for an MSP ID that set one up.
  const [sso, setSso] = useState<{ sso: boolean; name?: string }>({ sso: false });
  useEffect(() => {
    const id = msp.trim().toLowerCase();
    if (dev || id.length < 2) return setSso({ sso: false });
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      fetch(`/auth/methods?msp_id=${encodeURIComponent(id)}`, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : { sso: false }))
        .then((j: { sso?: boolean; name?: string }) => setSso({ sso: !!j.sso, name: j.name }))
        .catch(() => undefined);
    }, 350);
    return () => (clearTimeout(t), ctrl.abort());
  }, [msp, dev]);
  const keep = () => {
    try {
      localStorage.setItem("inlinea.msp_id", msp.trim().toLowerCase());
    } catch {
      /* private mode */
    }
  };
  return (
    <AuthShell product="Partner Portal" scene={PARTNER_SCENE} help={PARTNER_HELP}>
      <h1 className="auth-title">Sign in to the Partner Portal</h1>
      <p className="auth-lead">Use your MSP ID, your email and your password, then your authenticator.</p>
      <form className="auth-form" method="post" action={dev ? "/auth/dev" : "/auth/password"} onSubmit={() => (keep(), setBusy(true))}>
        {error && <AuthAlert>{error}</AuthAlert>}
        {dev && <AuthAlert tone="amber">Local development sign-in. It works only on this machine.</AuthAlert>}
        <div className="field">
          <label htmlFor="msp">MSP ID</label>
          <input id="msp" className="input mono" name="msp_id" autoComplete="organization" required value={msp} onChange={(e) => setMsp(e.target.value)} placeholder="arkan" spellCheck={false} autoCapitalize="none" autoFocus={!msp} />
          <span className="hint">The short name Inlinea gave your company.</span>
        </div>
        <div className="field">
          <label htmlFor="em">Work email</label>
          <input id="em" className="input" name="email" type="email" autoComplete="username" required placeholder="name@company.com" spellCheck={false} autoFocus={!!msp} />
        </div>
        {!dev && (
          <div className="field">
            <label htmlFor="pw">Password</label>
            <PasswordInput id="pw" name="password" required />
          </div>
        )}
        <input type="hidden" name="return" value={ret} />
        <Button type="submit" kind="primary" className="xl" busy={busy}>{dev ? "Sign in locally" : busy ? "Signing in…" : "Sign in"}</Button>
      </form>
      {!dev && sso.sso && (
        <div>
          <p className="auth-or">or</p>
          <a className="btn xl" data-idp-method href={`/auth/sso?msp_id=${encodeURIComponent(msp.trim())}&return=${encodeURIComponent(ret)}`} onClick={keep}>
            <Icon name="shield-keyhole" size={16} /> {sso.name ? `Continue with ${sso.name}` : "Continue with your company's identity provider"}
          </a>
        </div>
      )}
    </AuthShell>
  );
}
